import { authUserData } from ".";
import type { Payment, ECPayOrderParams, ECPayConfig, ECPayResult, RefundPaymentRequest, ECPayRefundResponse } from "../my-type";
import { PaymentStatus } from "../my-enum";
import {
  EcPayService as GeneratedEcPayService,
  PaymentService as GeneratedPaymentService,
  type CreatePaymentRequest,
  type RefundPaymentRequest as GeneratedRefundPaymentRequest,
  callApi,
  configureApiClient,
  fetchWithApiClient,
} from "../api/client";

const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL : "http://localhost:3000"

type ApiClientError = Error & { status?: number; body?: unknown };

const configurePaymentApiClient = () => {
  configureApiClient({
    tokenProvider: () => authUserData()?.jwtToken ?? null,
  });
};

const parseResponseJson = async <T,>(response: Response): Promise<T | null> => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text) as T;
};

export const getPaymentByOrderID = async (orderID: string) => {
  try {
    configurePaymentApiClient();
    const data = await callApi(
      () => GeneratedPaymentService.getApiPaymentGetPaymentByOrderId(orderID),
      "Failed to get payment by order id"
    );
    return data as Payment;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const createECPayPaymentByOrderId = async (orderId: string) => {
  const requestData: CreatePaymentRequest = {
    orderId: orderId
  };

  try {
    configurePaymentApiClient();
    const data = await callApi(
      () => GeneratedEcPayService.postApiEcPayCreatePayment(requestData),
      "Failed to create ECPay payment"
    );
    return data as Payment;
  } catch (error) {
    console.log(error);
    return null;
  }
};

// Helper function to generate CheckMacValue for ECPay by calling backend API
const generateCheckMacValue = async (params: Omit<ECPayOrderParams, 'CheckMacValue'>): Promise<string> => {
  console.log('Generating CheckMacValue with params:', params);
  //alert(JSON.stringify(params));
  try {
    configurePaymentApiClient();
    const data = await callApi(
      () => GeneratedEcPayService.postApiEcPayGenerateCheckMacValue(params as unknown as import("../generated/api").ECPayOrderParams),
      "Failed to generate CheckMacValue"
    );

    console.log('CheckMacValue generated successfully');

    const result = data as { checkMacValue?: string };
    if (result?.checkMacValue) {
      return result.checkMacValue;
    } else {
      throw new Error('Invalid response from CheckMacValue API');
    }

  } catch (error) {
    console.error('Failed to generate CheckMacValue:', error);
    throw new Error('Failed to generate CheckMacValue');
  }
};

// Create ECPay payment order and redirect to payment page
// Usage example:
// const config: ECPayConfig = {
//   merchantID: "2000132", // Your ECPay Merchant ID
//   isProduction: false // Set to true for production
// };
// await createECPayOrder("ORDER123", 1000, "Product Name", "Payment for order", config);
export const createECPayOrder = async (
  orderID: string,
  amount: number,
  itemName: string,
  tradeDesc: string,
  config: ECPayConfig,
  options?: {
    clientBackURL?: string;
    orderResultURL?: string;
    remark?: string;
    language?: "ENG" | "KOR" | "JPN" | "CHI";
  }
): Promise<boolean> => {
  try {

    // create payment by orderID first
    const payment = await createECPayPaymentByOrderId(orderID);
    if (!payment) {
      console.error('Failed to create ECPay payment record');
      return false;
    }

    console.log('ECPay payment record created:', payment);

    // Generate unique merchant trade number
    //const merchantTradeNo = `${orderID}_${Date.now()}`;
    const merchantTradeNo = `${payment.id.toString().padStart(6, '0')}`;
    //const merchantTradeNo = `${orderID}`;

    // Format date as required by ECPay (yyyy/MM/dd HH:mm:ss)
    const now = new Date();
    const merchantTradeDate = now.getFullYear() + '/' +
      String(now.getMonth() + 1).padStart(2, '0') + '/' +
      String(now.getDate()).padStart(2, '0') + ' ' +
      String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0') + ':' +
      String(now.getSeconds()).padStart(2, '0');

    // Prepare order parameters
    const orderParams: Omit<ECPayOrderParams, 'CheckMacValue'> = {
      MerchantID: config.merchantID,
      MerchantTradeNo: merchantTradeNo,
      MerchantTradeDate: merchantTradeDate,
      PaymentType: "aio",
      TotalAmount: amount,
      TradeDesc: tradeDesc,
      ItemName: itemName,
      ReturnURL: `${rootUrl}/api/ECPay/callback`,
      ChoosePayment: "Credit",
      EncryptType: 1,
      ClientBackURL: `${window.location.origin}/rider/order`,
      OrderResultURL: `${rootUrl}/api/ECPay/process-payment-result`,
      //Language: options?.language || "CHI",
      Remark: options?.remark || '',
      NeedExtraPaidInfo: "Y"
    };

    console.log('ECPay Order Params:', JSON.stringify(orderParams));

    // Generate CheckMacValue
    const checkMacValue = await generateCheckMacValue(orderParams);

    console.log('CheckMacValue:', checkMacValue);

    // Add CheckMacValue to parameters
    const finalParams: ECPayOrderParams = {
      ...orderParams,
      CheckMacValue: checkMacValue
    };

    console.log('Final ECPay Params:', JSON.stringify(finalParams));

    // Create form and submit to ECPay
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.isProduction
      ? 'https://payment.ecpay.com.tw/Cashier/AioCheckOut/V5'
      : 'https://payment-stage.ecpay.com.tw/Cashier/AioCheckOut/V5';

    // Add all parameters as hidden form fields
    Object.entries(finalParams).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    // Append form to body and submit
    document.body.appendChild(form);
    form.submit();

    // Clean up
    document.body.removeChild(form);

    return true;
  } catch (error) {
    console.error('ECPay order creation failed:', error);
    return false;
  }
};

// Process ECPay payment result callback
export const processECPayResult = async (ecpayResult: ECPayResult): Promise<boolean> => {
  try {
    configurePaymentApiClient();
    const response = await fetchWithApiClient("/api/ECPay/process-payment-result", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(ecpayResult),
    });
    const data = await parseResponseJson<{ success?: boolean }>(response);

    console.log('ECPay result processed:', data);

    if (response.ok && data?.success) {
      return true;
    } else {
      console.error('ECPay result processing failed:', data);
      return false;
    }

  } catch (error) {
    console.error('Failed to process ECPay result:', error);
    return false;
  }
};

// ECPay Refund Payment
export const refundECPayPayment = async (orderId: string, refundAmount?: number, refundReason?: string): Promise<ECPayRefundResponse> => {
  const authUser = authUserData();

  if (!authUser?.jwtToken) {
    return {
      success: false,
      message: "Authentication required",
      errorCode: 401,
      errorMessage: "No valid authentication token found"
    };
  }

  const requestData: RefundPaymentRequest = {
    orderId: orderId,
    ...(refundAmount && { refundAmount }),
    ...(refundReason && { refundReason })
  };

  try {
    console.log('ECPay refund request:', { orderId, refundAmount, refundReason });
    configurePaymentApiClient();
    const data = await callApi(
      () => GeneratedEcPayService.postApiEcPayRefund(requestData as GeneratedRefundPaymentRequest),
      "Refund failed"
    ) as {
      success?: boolean;
      message?: string;
      refundAmount?: number;
      tradeNo?: string;
      rtnCode?: number | string;
      errorCode?: number;
      errorMessage?: string;
      maintenanceWindow?: string;
    };

    console.log('ECPay refund response:', data);

    // Handle successful refund response
    if (data.success) {
      return {
        success: true,
        message: data.message || "Refund processed successfully",
        refundAmount: data.refundAmount,
        tradeNo: data.tradeNo,
        rtnCode: typeof data.rtnCode === "string" ? Number(data.rtnCode) : data.rtnCode
      };
    } else {
      // Handle business logic errors from ECPay
      return {
        success: false,
        message: data.message || "Refund failed",
        errorCode: data.errorCode || 0,
        errorMessage: data.errorMessage || data.message,
        maintenanceWindow: data.maintenanceWindow
      };
    }

  } catch (error) {
    console.error('ECPay refund failed:', error);
    const apiError = error as ApiClientError;
    const body = apiError?.body as { message?: string; errorMessage?: string } | string | undefined;
    const messageFromBody = typeof body === "string" ? body : body?.message;
    const errorMessageFromBody = typeof body === "string" ? body : body?.errorMessage;
    return {
      success: false,
      message: messageFromBody || "An unexpected error occurred",
      errorCode: apiError?.status || 0,
      errorMessage: errorMessageFromBody || (error instanceof Error ? error.message : "Unknown error")
    };
  }
};

// Helper function to check if a refund can be processed
export const canRefundPayment = async (orderId: string): Promise<{ canRefund: boolean; reason?: string; payment?: Payment }> => {
  try {
    const payment = await getPaymentByOrderID(orderId);

    if (!payment) {
      return {
        canRefund: false,
        reason: "Payment not found for this order"
      };
    }

    // Check if payment was successful (assuming status 3 means success based on other functions)
    if (payment.status !== PaymentStatus.Success) {
      return {
        canRefund: false,
        reason: "Payment is not in a refundable state",
        payment
      };
    }

    return {
      canRefund: true,
      payment
    };

  } catch (error) {
    console.error('Error checking refund eligibility:', error);
    return {
      canRefund: false,
      reason: "Unable to verify payment status"
    };
  }
};

// Convenience function for full refund
export const refundECPayPaymentFull = async (orderId: string, refundReason?: string): Promise<ECPayRefundResponse> => {
  return refundECPayPayment(orderId, undefined, refundReason);
};

// Convenience function for partial refund
export const refundECPayPaymentPartial = async (orderId: string, refundAmount: number, refundReason?: string): Promise<ECPayRefundResponse> => {
  if (refundAmount <= 0) {
    return {
      success: false,
      message: "Refund amount must be greater than 0",
      errorCode: 400,
      errorMessage: "Invalid refund amount"
    };
  }

  return refundECPayPayment(orderId, refundAmount, refundReason);
};
